import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useDmca, type DmcaInput } from "@/lib/compliance/useDmca";

const blank: DmcaInput = {
  reporter_name: "",
  reporter_email: "",
  reporter_address: "",
  copyright_owner: "",
  work_description: "",
  infringing_url: "",
  good_faith_statement: false,
  accuracy_statement: false,
  signature: "",
};

const DmcaForm = () => {
  const { submit, submitting } = useDmca();
  const [form, setForm] = useState<DmcaInput>(blank);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.reporter_name || !form.reporter_email) return toast.error("Name and email required.");
    if (!form.work_description) return toast.error("Describe the work.");
    if (!form.infringing_url) return toast.error("Provide the infringing URL.");
    if (!form.good_faith_statement || !form.accuracy_statement) {
      return toast.error("Both attestations are required by law.");
    }
    if (!form.signature) return toast.error("Sign your name to attest under penalty of perjury.");

    const { error } = await submit(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitted(true);
    setForm(blank);
    toast.success("DMCA report received. We'll review within 7 business days.");
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-success/30 bg-success/5 p-6 text-center">
        <p className="font-medium">Report received.</p>
        <p className="mt-2 text-sm text-muted-foreground">
          We'll review and respond within 7 business days at the email you provided.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => setSubmitted(false)}>
          Submit another report
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Use this form to report content you believe infringes your copyright.
        False claims may result in liability under 17 U.S.C. § 512(f).
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="dm-name">Your full name *</Label>
          <Input id="dm-name" value={form.reporter_name} maxLength={150}
                 onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} required />
        </div>
        <div>
          <Label htmlFor="dm-email">Your email *</Label>
          <Input id="dm-email" type="email" value={form.reporter_email} maxLength={200}
                 onChange={(e) => setForm({ ...form, reporter_email: e.target.value })} required />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="dm-addr">Mailing address</Label>
          <Input id="dm-addr" value={form.reporter_address} maxLength={300}
                 onChange={(e) => setForm({ ...form, reporter_address: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="dm-owner">Copyright owner (if not you)</Label>
          <Input id="dm-owner" value={form.copyright_owner} maxLength={200}
                 onChange={(e) => setForm({ ...form, copyright_owner: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="dm-work">Description of the copyrighted work *</Label>
          <Textarea id="dm-work" value={form.work_description} rows={3} maxLength={1000}
                    onChange={(e) => setForm({ ...form, work_description: e.target.value })} required />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="dm-url">URL of the allegedly infringing material *</Label>
          <Input id="dm-url" value={form.infringing_url} maxLength={500}
                 placeholder="https://wavlynq.com/u/…"
                 onChange={(e) => setForm({ ...form, infringing_url: e.target.value })} required />
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={form.good_faith_statement}
            onCheckedChange={(v) => setForm({ ...form, good_faith_statement: !!v })}
          />
          <span>
            I have a good faith belief that use of the material in the manner
            complained of is not authorized by the copyright owner, its agent, or
            the law.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={form.accuracy_statement}
            onCheckedChange={(v) => setForm({ ...form, accuracy_statement: !!v })}
          />
          <span>
            The information in this notification is accurate, and under penalty
            of perjury, I am the copyright owner or am authorized to act on
            behalf of the owner.
          </span>
        </label>
      </div>

      <div>
        <Label htmlFor="dm-sig">Electronic signature (type your full name) *</Label>
        <Input id="dm-sig" value={form.signature} maxLength={200}
               onChange={(e) => setForm({ ...form, signature: e.target.value })} required />
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit DMCA notice"}
        </Button>
      </div>
    </form>
  );
};

export default DmcaForm;
